import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ApiTitle } from "@lumi/data";
import { GripVertical, Trash2 } from "lucide-react";
import { Button } from "../../../shared/components/ui/button";
import { cn } from "../../../shared/components/ui/cn";
import { useCascadeFadeIn } from "../../../shared/hooks/useCascadeFadeIn";

/**
 * Collection item with required title for drag-drop functionality
 */
interface TitleCollectionItem {
  rank: number;
  title: ApiTitle;
}

interface DraggableCollectionItemProps {
  readonly item: TitleCollectionItem;
  readonly index: number;
  readonly onRemove: (titleId: string) => void;
  readonly collectionKey?: string;
}

/**
 * Rank threshold above which an item is considered "promoted" in new-releases
 */
const PROMOTED_RANK_THRESHOLD = 2000;

/**
 * DraggableCollectionItem - A collection item that can be reordered via drag-and-drop
 * Uses @dnd-kit/sortable for smooth drag-and-drop experience
 */
export function DraggableCollectionItem({ item, index, onRemove, collectionKey }: DraggableCollectionItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.title.id,
  });

  const { ref: fadeRef, isVisible } = useCascadeFadeIn({ index, threshold: 0.1, staggerDelay: 30 });

  // Select the best available poster image (horizontal tile preferred, then backdrop, then poster)
  const posterUrl = item.title.horizontalTileUrl ?? item.title.backdropUrlHd ?? item.title.posterUrlHd;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleRemove = () => {
    onRemove(item.title.id);
  };

  // Combine both refs: dnd-kit sortable (callback) + fade-in animation (RefObject)
  const setRefs = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    fadeRef.current = node;
  };

  return (
    <div
      ref={setRefs}
      style={style}
      className={cn(
        "bg-neutral-900/0 border-b border-b-neutral-700/0 p-4 flex items-center gap-4",
        "transition-all duration-500 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
        isDragging ? "border-lumi-accent shadow-lg z-50" : "border-neutral-800",
      )}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-neutral-500 hover:text-neutral-300 transition-colors p-1"
      >
        <GripVertical size={20} />
      </button>

      {/* Rank Badge */}
      <div className="flex-shrink-0 flex items-center justify-center">
        <span className="text-sm font-bold text-neutral-400">#{index + 1}</span>
      </div>

      {/* Poster */}
      {posterUrl && <img src={posterUrl} alt={item.title.name} className="w-32 h-18 object-cover rounded" />}

      {/* Title Info */}
      <div className="flex-1 min-w-0">
        <h5 className="text-white font-medium truncate">{item.title.name}</h5>
        <p className="text-sm text-neutral-400 mt-1">
          Rank: {item.rank}
          {item.title.year && ` • ${item.title.year}`}
          {item.title.runtimeMinutes && ` • ${item.title.runtimeMinutes} min`}
        </p>
      </div>

      {/* Promoted Badge */}
      {item.rank > PROMOTED_RANK_THRESHOLD && collectionKey === "new-releases" && (
        <div className="flex-shrink-0">
          <span className="px-2 py-1 text-lumi-accent text-xs font-semibold rounded">PROMOTED</span>
        </div>
      )}

      {/* Remove Button */}
      <Button
        onClick={handleRemove}
        variant="ghost-sm"
        size="sm"
        className="flex-shrink-0 text-white/50 hover:text-red-500 hover:bg-red-900/20"
        title="Remove from Collection"
      >
        <Trash2 size={18} />
      </Button>
    </div>
  );
}
